import { apiService } from './api';
import { ApiResponse } from '@/types/api';

// Profile data structure from the /api/v1/social/dashboard/profiles endpoint
export interface Profile {
  id: string;
  partitionKey: number;
  username: string;
  displayName: string;
  profileUrl: string;
  followerCount: number;
  followingCount: number;
  videoCount: number;
  verified: boolean;
  categoryId: number;
  categoryName: string;
  emvViewRate?: string;
  emvLikeRate?: string;
  emvCommentRate?: string;
  emvShareRate?: string;
  isActive?: boolean;
  platform: {
    id: number;
    name: string;
    displayName: string;
  };
}

export interface ProfilesResponse extends ApiResponse<{
  profiles: Profile[];
}> {}

export interface ProfileParams {
  customerId?: string;
  platformId?: number;
  isActive?: boolean;
  includeInactive?: boolean;
  page?: number;
  limit?: number;
}

export class ProfileService {
  /**
   * Get social media profiles with optional filtering
   */
  async getProfiles(params?: ProfileParams): Promise<ProfilesResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.customerId) {
      searchParams.append('customerId', params.customerId);
    }
    if (params?.platformId) {
      searchParams.append('platformId', params.platformId.toString());
    }
    if (params?.isActive !== undefined) {
      searchParams.append('isActive', params.isActive.toString());
    }
    if (params?.includeInactive !== undefined) {
      searchParams.append('includeInactive', params.includeInactive.toString());
    }
    if (params?.page) {
      searchParams.append('page', params.page.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }

    const query = searchParams.toString();
    const endpoint = `/social/dashboard/profiles${query ? `?${query}` : ''}`;
    
    return apiService.get<ProfilesResponse>(endpoint);
  }

  /**
   * Get profiles for a specific customer
   */
  async getCustomerProfiles(customerId: string): Promise<ProfilesResponse> {
    return this.getProfiles({ customerId });
  }

  /**
   * Get profiles for a specific platform
   */
  async getPlatformProfiles(platformId: number): Promise<ProfilesResponse> {
    return this.getProfiles({ platformId });
  }

  /**
   * Get active profiles only
   */
  async getActiveProfiles(): Promise<ProfilesResponse> {
    return this.getProfiles({ isActive: true });
  }
}

export const profileService = new ProfileService();